import { buyerPhoneSchema } from '@massilia/contracts'
import { z } from 'zod'

export const paymentMethods = [
  'cash',
  'credit-card',
  'installments',
  'vacation-cheque',
  'bank-transfer',
  'vacation-e-cheque',
] as const

export type PaymentMethodFormValue = (typeof paymentMethods)[number]

const floaPayTypes = ['3XG', '4XG'] as const
export type FloaPayTypeFormValue = (typeof floaPayTypes)[number]

const paymentTimings = ['PAY_NOW', 'PAY_LATER'] as const

// État initial du formulaire : aucun délai de paiement présélectionné,
// le schéma (required_error) force le choix à la soumission.
export type CheckoutFormInput = Omit<CheckoutFormType, 'paymentTiming'> & {
  paymentTiming: PaymentTimingFormValue | undefined
}

export type CheckoutFormType = z.infer<typeof checkoutFormSchema>

export type PaymentTimingFormValue = (typeof paymentTimings)[number]

export const checkoutFormSchema = z
  .object({
    buyerEmail: z.string().min(1, "L'adresse email est requise").email('Email invalide'),
    billingCity: z.string().optional(),
    billingCountryCode: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .optional(),
    billingStreet: z.string().optional(),
    billingZipCode: z.string().optional(),
    buyerPhone: buyerPhoneSchema,
    createAccount: z.boolean(),
    depositAmount: z.number().optional(),
    // Requis uniquement quand le prestataire installments est FLOA (flag runtime,
    // invisible du schéma statique) ; défaut '3XG' posé dans les defaultValues.
    floaPayType: z.enum(floaPayTypes).optional(),
    isPayingInMultipleTimes: z.boolean(),
    password: z.string().optional(),
    passwordConfirmation: z.string().optional(),
    paymentMethod: z.enum(paymentMethods),
    paymentTiming: z.enum(paymentTimings, {
      required_error: 'Veuillez choisir quand vous souhaitez régler votre voyage',
    }),
    subscribeToNotifications: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.createAccount) {
      if (!data.password || data.password.length < 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Le mot de passe doit contenir au moins 8 caractères',
          path: ['password'],
        })
      }

      if (!data.passwordConfirmation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Veuillez confirmer votre mot de passe',
          path: ['passwordConfirmation'],
        })
      }

      if (
        data.password &&
        data.passwordConfirmation &&
        data.password !== data.passwordConfirmation
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Les mots de passe ne correspondent pas',
          path: ['passwordConfirmation'],
        })
      }
    }
    if (data.paymentTiming === 'PAY_NOW' && data.paymentMethod === 'installments') {
      if (!data.billingStreet || data.billingStreet.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "L'adresse est requise",
          path: ['billingStreet'],
        })
      } else if (!/[A-Za-zÀ-ÿ]/.test(data.billingStreet)) {
        // SSP requires a non-numeric street name; reject purely-numeric inputs
        // before they reach the server.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Merci d'indiquer la rue (ex : 25 Rue de Paris)",
          path: ['billingStreet'],
        })
      }

      if (!data.billingZipCode || data.billingZipCode.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Le code postal est requis',
          path: ['billingZipCode'],
        })
      }

      if (!data.billingCity || data.billingCity.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La ville est requise',
          path: ['billingCity'],
        })
      }

      if (!data.billingCountryCode || data.billingCountryCode.value.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Le pays est requis',
          path: ['billingCountryCode'],
        })
      }
    }
  })

export type CheckoutFormValues = z.infer<typeof checkoutFormSchema>
